import React, { useMemo } from 'react';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import styled from 'styled-components';

import Dropdown from 'src/presentation/components/forms/Dropdown';
import Form, { useForm } from 'src/presentation/components/forms/Form';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import ivrsResource from 'src/actions/ivrs/ivrsResource';
import useActivateNumberForVoice from 'src/presentation/components/pages/channels/shared/hooks/useActivateNumberForVoice';
import voiceConfigurationResource from 'src/actions/channels/voiceConfigurationResource';
import { AvailablePhoneNumber } from 'src/actions/availableNumbers/availableNumbersResource';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { FieldWrapper } from '@ad-scripts/presentation/components/common/input_field';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import { NanoSecondsInputField } from 'src/presentation/components/pages/shared/NanoSecondsInputField';
import { Row } from 'src/presentation/components/forms/Layout';
import {
  StepWizardActionContainer,
  StepWizardFieldWithBorder,
} from 'src/presentation/components/pages/email/components/StepWizardLayout';
import { StageConfigState } from 'src/presentation/components/pages/channels/shared/helpers/stageConfiguration';
import {
  StyledBold,
  StyledDescription,
} from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/shared/elements';
import { formatPhone } from 'src/actions/shared/phonesHelper';
import { getPublishedIvrs } from 'src/presentation/components/pages/shared/helpers/ivrsHelper';
import { isLoadingStatus, useResourceValues } from 'src/presentation/hooks/useResource';
import { useResetFormState } from 'src/presentation/components/pages/channels/shared/hooks/useFormChange';
import { getAgentDesktopURL } from 'src/presentation/components/pages/shared/utils/Utils';

const useOnSave = (activateNumber, number) => {
  return ({ workingStateRef }) => {
    const currentWorkingState = workingStateRef.current;
    const workingStateToUpdate = {
      number,
      queueIvrId: currentWorkingState.queueIvrId || undefined,
      initialIvrId: currentWorkingState.initialIvrId || undefined,
      queueIvrWaitDuration: currentWorkingState.queueIvrWaitDuration || undefined,
    };

    activateNumber(workingStateToUpdate);
  };
};

const clearEmptyIvrsErrors = errors =>
  errors.flatMap(err =>
    (err.attr === 'queueIvrId' && err.emptyIvrs) || (err.attr === 'initialIvrId' && err.emptyIvrs) ? [] : [err]
  );

const initialWorkingState = { initialIvrId: '', queueIvrId: '', queueIvrWaitDuration: '' };

const SelectIvr = ({
  isActive,
  nextStep,
  stageConfig,
  previousStep,
  saveStep,
  phoneData,
}: {
  isActive?: boolean;
  nextStep?: () => void;
  previousStep?;
  stageConfig?: StageConfigState;
  saveStep?;
  phoneData: AvailablePhoneNumber;
}): JSX.Element | null => {
  const { add, validateWithRequiredOneIvr } = voiceConfigurationResource.effects;
  const { activateNumber, isPersisting } = useActivateNumberForVoice({
    add,
    isActive,
    nextStep,
    saveStep,
    validate: validateWithRequiredOneIvr,
    resetErrors: voiceConfigurationResource.actions.resetErrors,
  });
  const { errors } = useResourceValues(voiceConfigurationResource);
  const { values: ivrConfigurations, loadingState } = useResourceValues(ivrsResource);
  const onSaveHandler = useOnSave(activateNumber, phoneData.phoneNumber);
  const showErrorMessage = errors.filter(({ emptyIvrs }) => emptyIvrs).length === 2;
  const formContext = useForm({
    item: initialWorkingState,
    add,
    errors: showErrorMessage ? errors : clearEmptyIvrsErrors(errors),
    validate: validateWithRequiredOneIvr,
    resetErrors: voiceConfigurationResource.actions.resetErrors,
    onSave: onSaveHandler,
  });

  const ivrs = useMemo(() => getPublishedIvrs(ivrConfigurations), [ivrConfigurations]);
  const ivrsDropdownOptions = useMemo(
    () => [
      { label: 'None', value: '' },
      ...orderBy(
        ivrs.map(({ id, name, description }: any) => ({ label: name || description || id, value: id })),
        ['label'],
        ['asc']
      ),
    ],
    [ivrs]
  );
  const { workingState, onSave, updateState } = formContext;
  const queueIvr = get(workingState, 'queueIvrId');
  const isLoading = isLoadingStatus(loadingState);
  const organizationIvrsLink = getAgentDesktopURL('admin/ivrs');
  useResetFormState(initialWorkingState, updateState, stageConfig);
  return (
    <>
      <StepWizardFieldWithBorder>
        <H3 data-aid="wizard-title">Select IVR</H3>
        <StyledDescription>
          Select an{' '}
          <ExternalLink
            data-aid="helpLink"
            to="https://help.gladly.com/docs/initial-ivr-and-queue-ivr"
            label="initial IVR or queue IVR"
          />{' '}
          you want customers to get routed to when they call{' '}
          <StyledBold>{formatPhone(phoneData.phoneNumber)}</StyledBold>. Don’t see the IVR you’re looking for?{' '}
          <ExternalLink data-aid="helpLink" to={organizationIvrsLink} label="Confirm that it was published" />
        </StyledDescription>
        <Form context={formContext}>
          <StyledContainer>
            <Row>
              <Dropdown
                attr="initialIvrId"
                data-aid="initialIvrId"
                description="When customers call this number, they’re immediately routed to the first node (step) of this IVR."
                label="Initial IVR"
                options={ivrsDropdownOptions}
                placeholder="None"
                searchable
              />
            </Row>
          </StyledContainer>
          <StyledContainer>
            <Row>
              <Dropdown
                attr="queueIvrId"
                data-aid="queueIvrId"
                description="The IVR customers are routed to after a certain amount of time (seconds) has elapsed."
                label="Queue IVR"
                options={ivrsDropdownOptions}
                placeholder="None"
                searchable
              />
            </Row>
            {queueIvr && (
              <CallTimeoutRow>
                <StyledNanoSecondsInputField
                  attr="queueIvrWaitDuration"
                  className="nanoseconds-input"
                  data-aid="queueIvrWaitDuration"
                  description="The amount of time customers wait for an agent to become available. Timer resets once call banner is presented to an agent."
                  label="Queue IVR wait time (seconds)"
                />
              </CallTimeoutRow>
            )}
          </StyledContainer>
        </Form>
      </StepWizardFieldWithBorder>
      <StepWizardActionContainer>
        <Button
          data-aid="editor-save"
          type={Button.Types.PRIMARY}
          onClick={onSave}
          disabled={isLoading}
          isLoading={isPersisting}
        >
          Next
        </Button>
        <Button data-aid="editor-cancel" type={Button.Types.SECONDARY} onClick={previousStep}>
          Back
        </Button>
        {showErrorMessage && <StyledError>Select at least one IVR</StyledError>}
      </StepWizardActionContainer>
    </>
  );
};

const StyledRow = styled(Row)`
  margin-bottom: ${p => p.theme.spacing.stackXLarge};
`;

const StyledError = styled.div`
  color: ${p => p.theme.colors.red500};
`;

const StyledContainer = styled.div`
  display: flex;
  justify-content: space-between;

  > * {
    width: calc(50% - 10px);
  }
`;

const CallTimeoutRow = styled(StyledRow)`
  input {
    max-width: 62px;
  }
`;

const StyledNanoSecondsInputField = styled(NanoSecondsInputField)`
  ${FieldWrapper} {
    max-width: 80px;

    &:after {
      content: 's';
      padding: 1px 0;
      position: relative;
      right: 2px;
    }

    input {
      width: 100%;
    }
  }
`;

export default SelectIvr;
